import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Group,
    Loader,
    Paper,
    Progress,
    SimpleGrid,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core'
import {IconInfoCircle, IconUsers} from '@tabler/icons-react'
import {useMemo} from 'react'

import type {WorkerUtilizationRow} from './types'
import {WORKERS_UTILIZATION_HELP_TEXT} from './types'

type WorkersUtilizationCardProps = {
    workers: WorkerUtilizationRow[]
    isLoading: boolean
    isError: boolean
    error: unknown
    onRetry?: () => void
}

const WORKER_TYPE_COLORS = [
    'brand',
    'teal',
    'orange',
    'grape',
    'cyan',
    'red',
    'indigo',
    'violet',
] as const

type WorkerTypeSegment = WorkerUtilizationRow & {
    color: (typeof WORKER_TYPE_COLORS)[number]
    onJobsShareOfWorkforcePercent: number
    utilizationPercent: number
    available: number
}

type WorkerWorkforceSummary = {
    workforceTotal: number
    onJobsTotal: number
    availableTotal: number
    utilizationPercent: number
    availableShareOfWorkforcePercent: number
    segments: WorkerTypeSegment[]
}

export function buildWorkerWorkforceSummary(
    workers: WorkerUtilizationRow[],
): WorkerWorkforceSummary {
    const workforceTotal = workers.reduce((sum, entry) => sum + entry.total, 0)
    const onJobsTotal = workers.reduce((sum, entry) => sum + entry.onJobs, 0)
    const availableTotal = workforceTotal - onJobsTotal
    const utilizationPercent =
        workforceTotal === 0 ? 0 : Math.round((onJobsTotal / workforceTotal) * 100)
    const availableShareOfWorkforcePercent =
        workforceTotal === 0 ? 0 : (availableTotal / workforceTotal) * 100

    const segments = workers.map((entry, index) => ({
        ...entry,
        available: entry.total - entry.onJobs,
        color: WORKER_TYPE_COLORS[index % WORKER_TYPE_COLORS.length],
        onJobsShareOfWorkforcePercent:
            workforceTotal === 0 ? 0 : (entry.onJobs / workforceTotal) * 100,
        utilizationPercent: entry.total === 0 ? 0 : Math.round((entry.onJobs / entry.total) * 100),
    }))

    return {
        workforceTotal,
        onJobsTotal,
        availableTotal,
        utilizationPercent,
        availableShareOfWorkforcePercent,
        segments,
    }
}

function cardTitle() {
    return (
        <Group gap={4} wrap="nowrap">
            <Text fw={600} size="lg">
                Workers utilization
            </Text>
            <Tooltip label={WORKERS_UTILIZATION_HELP_TEXT} multiline w={320}>
                <ActionIcon
                    variant="transparent"
                    size="xs"
                    color="dimmed"
                    aria-label="Workers utilization definitions"
                >
                    <IconInfoCircle size={14} stroke={1.5} />
                </ActionIcon>
            </Tooltip>
        </Group>
    )
}

function WorkerTypeDetail({segment}: {segment: WorkerTypeSegment}) {
    return (
        <Box>
            <Text tt="uppercase" fz="xs" c="dimmed" fw={700}>
                {segment.typeName}
            </Text>
            <Group justify="space-between" align="flex-end" gap={0} mt="xs">
                <Text fw={700} fz="lg" lh={1}>
                    {segment.onJobs} / {segment.total}
                </Text>
                <Text c={segment.color} fw={700} fz="sm" lh={1}>
                    {segment.utilizationPercent}%
                </Text>
            </Group>
        </Box>
    )
}

function WorkerWorkforceChart({summary}: {summary: WorkerWorkforceSummary}) {
    const onJobsSegments = summary.segments
        .filter((segment) => segment.onJobs > 0)
        .map((segment) => (
            <Progress.Section
                key={segment.workerTypeId}
                value={segment.onJobsShareOfWorkforcePercent}
                color={segment.color}
                aria-label={`${segment.typeName} on jobs`}
            />
        ))

    return (
        <>
            <Group justify="space-between" mt="md">
                <Group align="baseline" gap="xs" wrap="wrap">
                    <Text fz="xl" fw={700} lh={1}>
                        {summary.onJobsTotal}
                    </Text>
                    <Text c="dimmed" fz="sm">
                        of {summary.workforceTotal} workers · {summary.utilizationPercent}% on jobs
                    </Text>
                </Group>
                <IconUsers size={22} color="var(--mantine-color-dimmed)" stroke={1.5} />
            </Group>

            <Progress.Root size="xl" mt="lg">
                {onJobsSegments}
                {summary.availableTotal > 0 && (
                    <Progress.Section
                        value={summary.availableShareOfWorkforcePercent}
                        color="gray.4"
                        aria-label="Available"
                    />
                )}
            </Progress.Root>

            <SimpleGrid cols={{base: 1, xs: 2, md: 4}} mt="md">
                {summary.segments.map((segment) => (
                    <WorkerTypeDetail key={segment.workerTypeId} segment={segment} />
                ))}
            </SimpleGrid>
        </>
    )
}

export function WorkersUtilizationCard({
    workers,
    isLoading,
    isError,
    error,
    onRetry,
}: WorkersUtilizationCardProps) {
    const summary = useMemo(() => buildWorkerWorkforceSummary(workers), [workers])

    if (isLoading) {
        return (
            <Paper withBorder p="xl" radius="md">
                <Stack gap="md">
                    {cardTitle()}
                    <Group justify="center" py="md">
                        <Loader size="sm" />
                    </Group>
                </Stack>
            </Paper>
        )
    }

    if (isError) {
        const message =
            error instanceof Error ? error.message : 'Failed to load workers utilization'
        return (
            <Paper withBorder p="xl" radius="md">
                <Stack gap="md">
                    {cardTitle()}
                    <Alert color="red" title="Could not load workers utilization">
                        {message}
                    </Alert>
                    {onRetry && (
                        <Button onClick={onRetry} variant="light">
                            Retry
                        </Button>
                    )}
                </Stack>
            </Paper>
        )
    }

    if (workers.length === 0) {
        return (
            <Paper withBorder p="xl" radius="md">
                <Stack gap="md">
                    {cardTitle()}
                    <Text c="dimmed" fz="sm">
                        No worker types to display.
                    </Text>
                </Stack>
            </Paper>
        )
    }

    return (
        <Paper withBorder p="xl" radius="md">
            <Stack gap="md">
                {cardTitle()}
                <WorkerWorkforceChart summary={summary} />
            </Stack>
        </Paper>
    )
}
